/**
 * Serviço de exportação de transcrições
 */

import { Transcription, AISummary } from '../types/transcription'
import { formatTimestamp, formatDuration } from './transcriptionAnalysis'
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  AlignmentType
} from 'docx'
import { saveAs } from 'file-saver'

/** Mapeamento de speakers personalizados */
export type SpeakerMapping = Record<string, string>

/**
 * Exporta transcrição para texto simples
 */
export function exportToText(
  transcription: Transcription, 
  options?: { includeSummary?: AISummary; speakerMapping?: SpeakerMapping }
): string {
  const lines: string[] = []
  const getSpeaker = (speaker: string) => options?.speakerMapping?.[speaker] || speaker

  // Cabeçalho
  lines.push('=' .repeat(60))
  lines.push('TRANSCRIÇÃO DE ÁUDIO/VÍDEO')
  lines.push('='.repeat(60))
  lines.push('')
  lines.push(`Título: ${transcription.title}`)
  lines.push(`Data: ${formatDate(transcription.date)}`)
  lines.push(`Duração: ${formatDuration(parseFloat(transcription.duration))}`)
  lines.push(`Fonte: ${transcription.transcript_source}`)

  if (transcription.participants.length > 0) {
    const participants = transcription.participants.map(p => getSpeaker(p))
    lines.push(`Participantes: ${participants.join(', ')}`)
  }

  if (transcription.topics.length > 0) {
    lines.push(`Tópicos: ${transcription.topics.join(', ')}`)
  }

  lines.push('')

  // Resumo IA (se disponível)
  if (options?.includeSummary) {
    lines.push('-'.repeat(60))
    lines.push('RESUMO (Gerado por IA)')
    lines.push('-'.repeat(60))
    lines.push('')
    lines.push(options.includeSummary.summary)
    lines.push('')

    if (options.includeSummary.keyPoints.length > 0) {
      lines.push('Pontos-chave:')
      options.includeSummary.keyPoints.forEach((point, i) => {
        lines.push(`  ${i + 1}. ${point}`)
      })
      lines.push('')
    }

    if (options.includeSummary.actionItems.length > 0) {
      lines.push('Ações Pendentes:')
      options.includeSummary.actionItems.forEach((item, i) => {
        lines.push(`  [ ] ${item}`)
      })
      lines.push('')
    }

    if (options.includeSummary.decisions.length > 0) {
      lines.push('Decisões:')
      options.includeSummary.decisions.forEach((decision, i) => {
        lines.push(`  • ${decision}`)
      })
      lines.push('')
    }
  }

  // Transcrição
  lines.push('-'.repeat(60))
  lines.push('TRANSCRIÇÃO COMPLETA')
  lines.push('-'.repeat(60))
  lines.push('')

  for (const segment of transcription.transcript) {
    const timestamp = `[${formatTimestamp(segment.start_time)}]`
    const speaker = segment.speaker ? `${getSpeaker(segment.speaker)}: ` : ''
    lines.push(`${timestamp} ${speaker}${segment.text}`)
    lines.push('')
  }

  lines.push('')
  lines.push('='.repeat(60))
  lines.push(`Documento gerado em ${new Date().toLocaleString('pt-BR')}`)
  lines.push('='.repeat(60))

  return lines.join('\n')
}

/**
 * Gera HTML da transcrição (base para PDF)
 */
export function exportToHTML(
  transcription: Transcription, 
  options?: { includeSummary?: AISummary; speakerMapping?: SpeakerMapping }
): string {
  const getSpeaker = (speaker: string) => options?.speakerMapping?.[speaker] || speaker
  
  const styles = `
    <style>
      body { font-family: 'Segoe UI', Arial, sans-serif; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px; }
      h1 { color: #1976d2; border-bottom: 2px solid #1976d2; padding-bottom: 10px; }
      h2 { color: #333; margin-top: 30px; }
      .meta { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
      .meta p { margin: 5px 0; }
      .meta strong { color: #555; }
      .summary { background: #e3f2fd; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
      .summary h3 { color: #1565c0; margin-top: 0; }
      .key-points, .actions, .decisions { margin: 15px 0; }
      .key-points li, .decisions li { margin: 5px 0; }
      .actions li { list-style: none; margin: 5px 0; }
      .actions li:before { content: "☐ "; }
      .segment { margin-bottom: 15px; padding: 10px; border-left: 3px solid #e0e0e0; }
      .segment:hover { border-left-color: #1976d2; background: #fafafa; }
      .timestamp { color: #666; font-size: 0.85em; font-family: monospace; }
      .speaker { font-weight: bold; color: #1976d2; }
      .text { margin-top: 5px; }
      .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e0e0e0; color: #666; font-size: 0.85em; }
    </style>
  `

  const participants = transcription.participants.map(p => getSpeaker(p))

  let html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>${escapeHtml(transcription.title)}</title>
  ${styles}
</head>
<body>
  <h1>📄 ${escapeHtml(transcription.title)}</h1>

  <div class="meta">
    <p><strong>Data:</strong> ${formatDate(transcription.date)}</p>
    <p><strong>Duração:</strong> ${formatDuration(parseFloat(transcription.duration))}</p>
    <p><strong>Fonte:</strong> ${transcription.transcript_source}</p>
    ${participants.length > 0 ? `<p><strong>Participantes:</strong> ${participants.join(', ')}</p>` : ''}
    ${transcription.topics.length > 0 ? `<p><strong>Tópicos:</strong> ${transcription.topics.join(', ')}</p>` : ''}
  </div>
`

  if (options?.includeSummary) {
    html += `
  <div class="summary">
    <h3>🤖 Resumo (Gerado por IA)</h3>
    <p>${escapeHtml(options.includeSummary.summary)}</p>

    ${options.includeSummary.keyPoints.length > 0 ? `
    <div class="key-points">
      <strong>📌 Pontos-chave:</strong>
      <ul>
        ${options.includeSummary.keyPoints.map(p => `<li>${escapeHtml(p)}</li>`).join('\n')}
      </ul>
    </div>
    ` : ''}

    ${options.includeSummary.actionItems.length > 0 ? `
    <div class="actions">
      <strong>✅ Ações Pendentes:</strong>
      <ul>
        ${options.includeSummary.actionItems.map(a => `<li>${escapeHtml(a)}</li>`).join('\n')}
      </ul>
    </div>
    ` : ''}

    ${options.includeSummary.decisions.length > 0 ? `
    <div class="decisions">
      <strong>⚖️ Decisões:</strong>
      <ul>
        ${options.includeSummary.decisions.map(d => `<li>${escapeHtml(d)}</li>`).join('\n')}
      </ul>
    </div>
    ` : ''}
  </div>
`
  }

  html += `
  <h2>📝 Transcrição Completa</h2>
`

  for (const segment of transcription.transcript) {
    const speaker = getSpeaker(segment.speaker || 'Não identificado')
    html += `
  <div class="segment">
    <span class="timestamp">[${formatTimestamp(segment.start_time)}]</span>
    <span class="speaker">${escapeHtml(speaker)}</span>
    <div class="text">${escapeHtml(segment.text)}</div>
  </div>
`
  }

  html += `
  <div class="footer">
    Documento gerado em ${new Date().toLocaleString('pt-BR')}
  </div>
</body>
</html>
`

  return html
}

/**
 * Gera estrutura para DOCX
 */
export interface DocxContent {
  title: string
  metadata: { label: string; value: string }[]
  summary?: {
    text: string
    keyPoints: string[]
    actionItems: string[]
    decisions: string[]
  }
  segments: { timestamp: string; speaker: string; text: string }[]
  generatedAt: string
}

export function prepareDocxContent(transcription: Transcription, options?: { includeSummary?: AISummary }): DocxContent {
  return {
    title: transcription.title,
    metadata: [
      { label: 'Data', value: formatDate(transcription.date) },
      { label: 'Duração', value: formatDuration(parseFloat(transcription.duration)) },
      { label: 'Fonte', value: transcription.transcript_source },
      ...(transcription.participants.length > 0 ? [{ label: 'Participantes', value: transcription.participants.join(', ') }] : []),
      ...(transcription.topics.length > 0 ? [{ label: 'Tópicos', value: transcription.topics.join(', ') }] : [])
    ],
    summary: options?.includeSummary ? {
      text: options.includeSummary.summary,
      keyPoints: options.includeSummary.keyPoints,
      actionItems: options.includeSummary.actionItems,
      decisions: options.includeSummary.decisions
    } : undefined,
    segments: transcription.transcript.map(seg => ({
      timestamp: formatTimestamp(seg.start_time),
      speaker: seg.speaker || 'Não identificado',
      text: seg.text
    })),
    generatedAt: new Date().toLocaleString('pt-BR')
  }
}

/**
 * Download de arquivo
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Exporta como TXT
 */
export function downloadAsText(
  transcription: Transcription, 
  options?: { includeSummary?: AISummary; speakerMapping?: SpeakerMapping }
): void {
  const content = exportToText(transcription, options)
  const filename = `${sanitizeFilename(transcription.title)}.txt`
  downloadFile(content, filename, 'text/plain;charset=utf-8')
}

/**
 * Exporta como HTML
 */
export function downloadAsHTML(
  transcription: Transcription, 
  options?: { includeSummary?: AISummary; speakerMapping?: SpeakerMapping }
): void {
  const content = exportToHTML(transcription, options)
  const filename = `${sanitizeFilename(transcription.title)}.html`
  downloadFile(content, filename, 'text/html;charset=utf-8')
}

/**
 * Exporta como DOCX (Word)
 */
export async function downloadAsDocx(
  transcription: Transcription, 
  options?: { includeSummary?: AISummary; speakerMapping?: SpeakerMapping }
): Promise<void> {
  const getSpeaker = (speaker: string) => options?.speakerMapping?.[speaker] || speaker
  const participants = transcription.participants.map(p => getSpeaker(p))
  
  const children: Paragraph[] = []

  // Título
  children.push(
    new Paragraph({
      text: transcription.title,
      heading: HeadingLevel.TITLE,
      spacing: { after: 400 }
    })
  )

  // Metadados
  children.push(
    new Paragraph({
      text: 'INFORMAÇÕES DO DOCUMENTO',
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 200 }
    })
  )

  const metaItems = [
    { label: 'Data', value: formatDate(transcription.date) },
    { label: 'Duração', value: formatDuration(parseFloat(transcription.duration)) },
    { label: 'Fonte', value: transcription.transcript_source },
    ...(participants.length > 0 ? [{ label: 'Participantes', value: participants.join(', ') }] : []),
    ...(transcription.topics.length > 0 ? [{ label: 'Tópicos', value: transcription.topics.join(', ') }] : [])
  ]

  for (const item of metaItems) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${item.label}: `, bold: true }),
          new TextRun({ text: item.value })
        ],
        spacing: { after: 100 }
      })
    )
  }

  // Resumo IA (se disponível)
  if (options?.includeSummary) {
    children.push(
      new Paragraph({
        text: 'RESUMO (Gerado por IA)',
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 200 }
      })
    )

    children.push(
      new Paragraph({
        text: options.includeSummary.summary,
        spacing: { after: 200 }
      })
    )

    if (options.includeSummary.keyPoints.length > 0) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: 'Pontos-chave:', bold: true })],
          spacing: { before: 200, after: 100 }
        })
      )
      for (const point of options.includeSummary.keyPoints) {
        children.push(
          new Paragraph({
            text: `• ${point}`,
            spacing: { after: 50 }
          })
        )
      }
    }

    if (options.includeSummary.actionItems.length > 0) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: 'Ações Pendentes:', bold: true })],
          spacing: { before: 200, after: 100 }
        })
      )
      for (const item of options.includeSummary.actionItems) {
        children.push(
          new Paragraph({
            text: `☐ ${item}`,
            spacing: { after: 50 }
          })
        )
      }
    }

    if (options.includeSummary.decisions.length > 0) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: 'Decisões:', bold: true })],
          spacing: { before: 200, after: 100 }
        })
      )
      for (const decision of options.includeSummary.decisions) {
        children.push(
          new Paragraph({
            text: `✓ ${decision}`,
            spacing: { after: 50 }
          })
        )
      }
    }
  }

  // Transcrição completa
  children.push(
    new Paragraph({
      text: 'TRANSCRIÇÃO COMPLETA',
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 200 }
    })
  )

  for (const segment of transcription.transcript) {
    const speaker = getSpeaker(segment.speaker || 'Não identificado')
    
    children.push(
      new Paragraph({
        children: [
          new TextRun({ 
            text: `[${formatTimestamp(segment.start_time)}] `, 
            color: '666666',
            size: 20 // 10pt
          }),
          new TextRun({ 
            text: `${speaker}: `, 
            bold: true,
            color: '1976d2'
          }),
          new TextRun({ text: segment.text })
        ],
        spacing: { after: 150 }
      })
    )
  }

  // Rodapé
  children.push(
    new Paragraph({
      children: [
        new TextRun({ 
          text: `Documento gerado em ${new Date().toLocaleString('pt-BR')}`,
          italics: true,
          color: '666666',
          size: 18
        })
      ],
      spacing: { before: 400 },
      alignment: AlignmentType.CENTER
    })
  )

  const doc = new Document({
    sections: [{
      properties: {},
      children
    }]
  })

  const blob = await Packer.toBlob(doc)
  saveAs(blob, `${sanitizeFilename(transcription.title)}.docx`)
}

// Helpers

function formatDate(timestamp: string): string {
  const date = new Date(parseInt(timestamp, 10))
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function sanitizeFilename(name: string): string {
  return name
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 100)
}
